/**
 * Cover the groups that existed before the recovery key did.
 *
 * A recovery key protects nothing retroactively, and cannot: the server holds
 * no group key, so only a browser can seal one to it. Registering a key
 * therefore leaves every existing group uncovered, and the admin panel says so
 * — "11 groups have no escrow copy" — without offering any way to change it.
 *
 * This is that way. It is on the account screen rather than in the admin panel
 * because it is not an administrator's job: only somebody who can *open* a
 * group can seal it, and that is its members. An administrator with a recovery
 * key and no membership cannot help.
 *
 * Each group is opened and sealed one at a time. Opening is an RSA unwrap and
 * sealing is another; forty of them at once on a phone is a stalled tab, and
 * there is no hurry — this runs once in the life of an installation.
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc, trpcClient } from "../../lib/trpc.js";
import { useT } from "../../lib/i18n.js";
import { hasGroupKey, openGroupKey } from "../../lib/keyring.js";
import { activeRecoveryKey, escrowGroup } from "../group/escrow.js";
import { Button } from "../../components/ui/Button.js";
import { useToast } from "../../components/ui/Toast.js";

export function ProtectGroups() {
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  const pending = useQuery(trpc.groups.unescrowed.queryOptions());
  const groups = pending.data ?? [];

  /*
   * Nothing to say when there is nothing to do. This is a repair for a
   * transition, and a permanent "0 groups need protecting" panel on the
   * account screen would outlive its usefulness by years.
   */
  if (groups.length === 0) return null;

  async function protectAll(): Promise<void> {
    const key = await activeRecoveryKey();
    if (!key) {
      toast(t("There is no recovery key set up yet."), { variant: "danger" });
      return;
    }

    setProgress({ done: 0, total: groups.length });
    let sealed = 0;

    for (const [index, group] of groups.entries()) {
      try {
        /*
         * Opened first, and only here — this is a screen somebody may never
         * have visited a given group from, so the key is not already cached.
         */
        if (!hasGroupKey(group.groupId)) {
          const grant = await trpcClient.groups.groupKey.query({
            groupId: group.groupId,
          });
          if (grant.wrappedGroupKey) {
            await openGroupKey(group.groupId, grant.wrappedGroupKey);
          }
        }

        if (hasGroupKey(group.groupId) && (await escrowGroup(group.groupId, key))) {
          sealed += 1;
        }
      } catch {
        /*
         * A group this account cannot open — invited but never entered, or
         * awaiting a key grant. Skipped rather than reported: it is an
         * ordinary state and somebody else in that group can cover it.
         */
      } finally {
        /* In a finally, so a group that could not be opened still advances the
           count rather than leaving the button stuck partway. */
        setProgress({ done: index + 1, total: groups.length });
      }
    }

    setProgress(null);
    await queryClient.invalidateQueries({
      queryKey: trpc.groups.unescrowed.queryKey(),
    });

    toast(
      sealed === groups.length
        ? t("All your groups are protected now.")
        : `${String(sealed)} of ${String(groups.length)} protected. The rest need somebody who can open them.`,
    );
  }

  return (
    <section className="fc-account__section">
      <h2>{t("Protect your groups")}</h2>

      <p className="fc-account__hint">
        {t(
          "These groups were made before the recovery key existed. Until each one is sealed to it, a group whose members all lose their passwords cannot be recovered by anyone — including us. Nothing is uploaded but a key only the recovery shares can open.",
        )}
      </p>

      <p className="fc-account__hint">
        {groups.length === 1
          ? t("One group is not protected yet.")
          : `${String(groups.length)} ${t("groups are not protected yet.")}`}
      </p>

      <Button
        variant="secondary"
        loading={progress !== null}
        onClick={() => void protectAll()}
      >
        {progress
          ? `${String(progress.done)} / ${String(progress.total)}`
          : t("Protect them")}
      </Button>
    </section>
  );
}
